import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Typography,
  TextField,
  Stack,
  Chip,
  Button,
  Divider,
  useTheme,
  InputAdornment,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import SearchIcon from '@mui/icons-material/Search';
import { keyframes } from '@mui/system';
import { useColorMode } from '../../../../context/ThemeContext';

type FaqItem = {
  id: string;
  q: string;
  a: ReactNode;
  tags?: string[];
};

export default function FAQ() {
  const theme = useTheme();
  const { mode } = useColorMode();
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState<string[]>([]);
  const itemRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const fadeUp = keyframes`
    from { opacity: 0; transform: translateY(8px) }
    to   { opacity: 1; transform: translateY(0) }
  `;

  const faqs: FaqItem[] = useMemo(
    () => [
      {
        id: 'mi-ez-az-analyzer',
        q: 'Mi az Analyzer és mire jó?',
        a: (
          <>
            Az Analyzer linkekhez kapcsolódó kommenteket gyűjt és <b>0–10 skálán hangulatelemzést</b> készít.
            Grafikonokkal és statisztikákkal segít megérteni a közösségi visszajelzéseket.
          </>
        ),
        tags: ['alapok', 'hangulatelemzés'],
      },
      {
        id: 'milyen-linkeket-fogad',
        q: 'Milyen forrásokat támogat a rendszer?',
        a: (
          <>
            Jelenleg: <b>Reddit</b>, <b>YouTube</b>, <b>Google Maps</b>, <b>Stack Overflow</b>, <b>X / Twitter</b>,
            és <b>GyakoriKérdések</b>. A főoldalon csak illeszd be a hivatkozást, és elindul az elemzés.
          </>
        ),
        tags: ['források', 'linkek'],
      },
      {
        id: 'hogyan-mukodik-a-pontozas',
        q: 'Mit jelent a 0–10 közötti pontszám?',
        a: (
          <>
            <b>0</b> nagyon negatív, <b>10</b> nagyon pozitív. A rendszer a kommentek szövegét elemzi, majd aggregál
            (átlag, eloszlás, trendek), így gyorsan látható az összkép és a kiugró vélemények.
          </>
        ),
        tags: ['metodika', 'értékelés'],
      },
      {
        id: 'mennyi-ido-az-elemzes',
        q: 'Mennyi ideig tart egy elemzés?',
        a: (
          <>
            A forrástól és a kommentek számától függ. A háttérben egy sorfeldolgozó (RabbitMQ + worker) végzi a
            feladatot, az állapot <i>Folyamatban</i> → <i>Kész</i> módon frissül a “Saját elemzések” listában.
          </>
        ),
        tags: ['teljesítmény', 'háttérfolyamat', 'RabbitMQ'],
      },
      {
        id: 'hol-talalom-az-eredmenyeket',
        q: 'Hol látom az elkészült eredményeket?',
        a: (
          <>
            A <b>Saját elemzések</b> oldalon (JobList) listázva vannak. Egy elemzésre kattintva megnyílik a
            részletező (statisztikák, grafikonok, kommentek).
          </>
        ),
        tags: ['navigáció', 'eredmények'],
      },
      {
        id: 'bejelentkezes-es-regisztracio',
        q: 'Hogyan tudok regisztrálni / bejelentkezni?',
        a: (
          <>
            A <b>Regisztráció</b> és <b>Belépés</b> űrlapok a fejlécből érhetők el. A hitelesítést a szerver oldalon az
            <code>AuthController</code> kezeli. Első belépés után már létrehozhatsz elemzéseket.
          </>
        ),
        tags: ['auth', 'fiók'],
      },
      {
        id: 'profilkep-es-megjeleno-nev',
        q: 'Hogyan állíthatok be profilképet és megjelenő nevet?',
        a: (
          <>
            A <b>Beállítások</b> oldalon (Settings) tölthetsz fel profilképet, és szerkesztheted a megjelenő nevet.
            A fejléchez kötött komponensek frissülnek, a képekhez cache-buster is társul, hogy azonnal látszódjon a
            módosítás.
          </>
        ),
        tags: ['profil', 'kép', 'settings'],
      },
      {
        id: 'nem-tamogatott-link',
        q: 'Mit tegyek, ha egy linket nem fogad el?',
        a: (
          <>
            Győződj meg róla, hogy a link <b>nyilvános</b> és a támogatott források egyike. Privát/védett tartalmak
            (pl. privát YouTube-videó) nem elemezhetők, mert a hozzászólások nem elérhetők.
          </>
        ),
        tags: ['hibák', 'link'],
      },
      {
        id: 'adatkezeles',
        q: 'Hogyan kezeli az adatokat az alkalmazás?',
        a: (
          <>
            A rendszer a megadott linkhez tartozó <b>nyilvános kommenteket</b> olvassa be és elemzi. A felhasználói
            fiókhoz tartozó adatok (pl. e-mail) csak a bejelentkezéshez és a saját elemzésekhez szükségesek.
          </>
        ),
        tags: ['privacy', 'adatkezelés'],
      },
      {
        id: 'nyelvek',
        q: 'Milyen nyelvű kommenteket tud elemezni?',
        a: (
          <>
            Több nyelvvel boldogul, köztük magyarral és angollal is. Vegyes nyelvű szálaknál a metrikák a teljes halmazra
            aggregálódnak.
          </>
        ),
        tags: ['nyelv', 'metodika'],
      },
      {
        id: 'szures-es-kommentlista',
        q: 'Lehet szűrni a kommenteket (pozitív/negatív stb.)?',
        a: (
          <>
            Igen: a részletező nézetben a kommentek <b>pontszám</b> és szövegrészlet alapján átszűrhetők és
            rendezhetők, így könnyebb a releváns véleményeket megtalálni.
          </>
        ),
        tags: ['szűrés', 'kommentek'],
      },
      {
        id: 'hibajelentes',
        q: 'Hogyan jelezhetek hibát vagy kérhetek új funkciót?',
        a: (
          <>
            Írj egy rövid leírást és a problémás linket. Ha célszerű, mellékelj képernyőképet is. (A projektben külön
            issue-ként is felvehető.)
          </>
        ),
        tags: ['support', 'feature'],
      },
    ],
    []
  );

  const filteredFaqs = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return faqs;
    return faqs.filter((f) => {
      const text =
        (typeof f.a === 'string' ? f.a : '') +
        ' ' +
        f.q +
        ' ' +
        (f.tags ?? []).join(' ');
      return text.toLowerCase().includes(q);
    });
  }, [faqs, query]);

  // Hash-link támogatás (#faq-...)
  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (!hash) return;
    const target = faqs.find((f) => f.id === hash);
    if (target) {
      setExpanded((prev) => Array.from(new Set([...prev, target.id])));
      setTimeout(() => {
        itemRefs.current[target.id]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 50);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const expandAll = () => setExpanded(filteredFaqs.map((f) => f.id));
  const collapseAll = () => setExpanded([]);
  const toggle = (id: string) =>
    setExpanded((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  return (
    <Box
      sx={{
        mt: { xs: 3, md: 5 },
        maxWidth: 980,
        mx: 'auto',
        p: { xs: 3, md: 4 },
        backgroundColor: mode === 'dark' ? 'grey.900' : 'grey.50',
        borderRadius: 3,
        boxShadow: 4,
        animation: `${fadeUp} .35s ease`,
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} sx={{ mb: 2 }}>
        <HelpOutlineIcon fontSize="small" />
        <Typography variant="h4" textAlign="center">
          GYIK
        </Typography>
      </Stack>

      <Typography
        variant="body1"
        sx={{ textAlign: 'center', color: 'text.secondary', maxWidth: 760, mx: 'auto', mb: 3 }}
      >
        Gyors válaszok az Analyzer használatához, feldolgozáshoz és eredmények értelmezéséhez.
      </Typography>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        sx={{ mb: 2, alignItems: { xs: 'stretch', sm: 'center' }, justifyContent: 'space-between' }}
      >
        <TextField
          fullWidth
          placeholder="Keresés a kérdések között…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
        />
        <Stack direction="row" spacing={1} sx={{ alignSelf: { xs: 'stretch', sm: 'auto' } }}>
          <Button variant="outlined" onClick={expandAll}>
            Összes nyit
          </Button>
          <Button variant="outlined" onClick={collapseAll}>
            Összes zár
          </Button>
        </Stack>
      </Stack>

      {!!query && (
        <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
          Találatok: <b>{filteredFaqs.length}</b> / {faqs.length}
        </Typography>
      )}

      <Divider sx={{ mb: 2, opacity: 0.3 }} />

      <Stack spacing={1.5}>
        {filteredFaqs.map((f, idx) => (
          <Accordion
            key={f.id}
            expanded={expanded.includes(f.id)}
            onChange={() => toggle(f.id)}
            disableGutters
            sx={{
              backgroundColor: mode === 'dark' ? 'grey.800' : 'grey.100',
              borderRadius: 2,
              px: 1,
              '&::before': { display: 'none' },
              boxShadow: 0,
              transition: 'transform .2s ease, box-shadow .2s ease, background-color .2s ease',
              animation: `${fadeUp} .35s ease`,
              animationDelay: `${idx * 40}ms`,
              animationFillMode: 'backwards',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: theme.shadows[4],
                background:
                  mode === 'dark'
                    ? `linear-gradient(180deg, ${theme.palette.grey[800]}, ${theme.palette.grey[900]})`
                    : `linear-gradient(180deg, ${theme.palette.grey[100]}, ${theme.palette.grey[50]})`,
              },
            }}
            ref={(el: HTMLDivElement | null) => {
              itemRefs.current[f.id] = el;
            }}
            id={f.id}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                py: 1, // kicsi függőleges padding
                '& .MuiAccordionSummary-content': {
                  alignItems: 'flex-start',
                  my: 0.5,
                  pb: 0.5, // ⬅️ kis hely alul, a chipek után is érvényesül
                },
              }}
            >
              <Stack spacing={1} sx={{ width: '100%' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {f.q}
                </Typography>

                {!!f.tags?.length && (
                  <Stack
                    direction="row"
                    useFlexGap
                    sx={{
                      flexWrap: 'wrap',
                      gap: 1,
                      mt: 0.25, // kis elválasztás a cím és a chipek között
                    }}
                  >
                    {f.tags?.map((t) => (
                      <Chip
                        key={t}
                        size="small"
                        label={t}
                        sx={{
                          bgcolor: mode === 'dark' ? 'grey.700' : 'grey.200',
                          flexShrink: 0,
                        }}
                      />
                    ))}
                  </Stack>
                )}
              </Stack>
            </AccordionSummary>

            <AccordionDetails>
              <Typography variant="body2">{f.a}</Typography>
            </AccordionDetails>
          </Accordion>
        ))}

        {!filteredFaqs.length && (
          <Box
            sx={{
              p: 4,
              textAlign: 'center',
              color: 'text.secondary',
              backgroundColor: mode === 'dark' ? 'grey.800' : 'grey.100',
              borderRadius: 2,
            }}
          >
            Nincs találat a(z) “{query}” keresésre. Próbálj más kulcsszót.
          </Box>
        )}
      </Stack>

      <Typography variant="caption" sx={{ display: 'block', mt: 3, color: 'text.secondary' }}>
        Tipp: bármelyik kérdés külön linkelhető, pl. <code>#mi-ez-az-analyzer</code>.
      </Typography>
    </Box>
  );
}
